import { TFunction } from 'i18next';
import { getDiscountPercent } from '@/entities/cart';
import type { QuantityDiscount } from '@/entities/product';

interface QuantityOption {
  label: string;
  value: string;
  totalPrice: number;
  discount: number;
  description: string;
  discountPrice: number;
}

type Params = {
  slug: string;
  price: number;
  t: TFunction;
  quantityDiscounts?: QuantityDiscount[];
};

const createQuantityOptions = ({
  slug,
  price,
  t,
  quantityDiscounts = [],
}: Params): QuantityOption[] => {
  const baseOption: QuantityOption = {
    label: t('store.product.quantity.single', { count: 1 }),
    value: '1',
    totalPrice: price,
    discount: 0,
    description: '',
    discountPrice: 0,
  };

  const discountOptions = quantityDiscounts
    .sort((a, b) => a.minQuantity - b.minQuantity)
    .map(({ minQuantity, discountPrice = 0, discountPercent }) => {
      const discount = getDiscountPercent(slug, minQuantity, quantityDiscounts);

      return {
        label: t('store.product.quantity.multiple', { count: minQuantity }),
        value: String(minQuantity),
        totalPrice: price * minQuantity,
        discount,
        description: `${discountPercent}% off for ${minQuantity}+ items`,
        discountPrice,
      };
    });

  return [baseOption, ...discountOptions];
};

const getDiscountDescription = (quantityDiscounts?: QuantityDiscount[]): string | undefined => {
  return quantityDiscounts && quantityDiscounts.length > 0
    ? 'Save more when you buy multiple items'
    : undefined;
};

const prepareQuantity = (quantity?: string | string[] | null): number => {
  const parsedQuantity = Array.isArray(quantity) ? Number(quantity[0]) : Number(quantity);
  return isNaN(parsedQuantity) || parsedQuantity < 1 ? 1 : parsedQuantity;
};

export type { QuantityOption };
export { createQuantityOptions, getDiscountDescription, prepareQuantity };
